import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { login, logout, resendVerificationEmail, sendPasswordResetEmail } from './auth';
import { Link, useNavigate } from 'react-router-dom';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    try {
      await login(email, password);
      navigate('/match-predictor');
    } catch (error) {
      if (error.message === 'EMAIL_NOT_VERIFIED') {
        await logout();
        setError(
          <span>
            Please verify your email before logging in (remember to check your spam folder).{' '}
            <button
              onClick={handleResendVerification}
              className="text-premier-purple underline hover:text-premier-pink"
            >
              Resend verification email
            </button>
          </span>
        );
      } else {
        setError(error.message);
      }
    }
  };

  const handleResendVerification = async () => {
    try {
      await resendVerificationEmail();
      setMessage('Verification email sent. Please check your inbox.');
      setError(null);
    } catch (error) {
      setError('Failed to resend verification email. Please try again.');
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError('Please enter your email address before requesting a password reset.');
      return;
    }
    try {
      await sendPasswordResetEmail(email);
      setMessage('Password reset email sent. Please check your inbox.');
      setError(null);
    } catch (error) {
      setError('Failed to send password reset email. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-100 to-purple-100 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-8 bg-white p-10 rounded-xl shadow-lg"
      >
        <div>
          <motion.h2 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mt-6 text-center text-3xl font-extrabold text-premier-purple"
          >
            Log in to your account
          </motion.h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-premier-pink focus:border-premier-pink focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-premier-pink focus:border-premier-pink focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </motion.div>
          </div>

          {error && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-600 text-sm font-medium"
            >
              {error}
            </motion.div>
          )}

          {message && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-green-600 text-sm font-medium"
            >
              {message}
            </motion.div>
          )}

          <div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-premier-purple hover:bg-premier-pink focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-premier-pink transition-colors duration-300"
            >
              Log In
            </motion.button>
          </div>
        </form>
        <div className="text-center space-y-2">
          <Link to="/signup" className="block font-medium text-premier-purple hover:text-premier-pink">
            Don't have an account? Sign up
          </Link>
          <button
            onClick={handlePasswordReset}
            className="font-medium text-premier-purple hover:text-premier-pink"
          >
            Forgot your password?
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;